<template>
    <Popover :disabled="disabled" @open="handleOpen" @close="handleClose">
        <template #trigger>
            <span class="driver-popover-default-trigger">
                <slot name="trigger">{{ displayDriverName }}</slot>
            </span>
        </template>
        <template #content="{ close }">
            <div class="driver-popover-content" v-if="setCloseFunction(close)">
                <slot
                    :close="close"
                    :driverName="displayDriverName"
                    :driverNumber="driverNumber"
                    :driverId="driverId"
                >
                    <div class="driver-info">
                        <div class="driver-name-row">
                            <div class="driver-name">{{ displayDriverName }}</div>

                            <Button
                                v-if="currentRoute === 'shifts'"
                                v-tooltip="'Fahrer ändern'"
                                variant="icon"
                                size="small"
                                @onClick="handleChangeDriver(close)"
                            >
                                <i class="ri-edit-line"></i>
                            </Button>
                        </div>
                        <div v-if="!showDriverNumber" class="driver-number">
                            ID: {{ driverNumber }}
                        </div>
                    </div>
                </slot>

                <div class="driver-popover-actions">
                    <Button
                        v-if="showRevenueButton"
                        v-tooltip="getRevenueTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageRevenue && isDriverFiltered"
                        :class="{
                            'current-page': isCurrentPageRevenue && isDriverFiltered,
                        }"
                        @onClick="handleNavigate('revenue', close)"
                    >
                        <i class="ri-money-dollar-circle-line"></i>
                        <span class="button-hotkey">U</span>
                    </Button>

                    <Button
                        v-if="showWorkingTimeButton"
                        v-tooltip="getWorkingTimeTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageWorkingTime && isDriverFiltered"
                        :class="{
                            'current-page': isCurrentPageWorkingTime && isDriverFiltered,
                        }"
                        @onClick="handleNavigate('working-time', close)"
                    >
                        <i class="ri-time-line"></i>
                        <span class="button-hotkey">A</span>
                    </Button>

                    <Button
                        v-if="showShiftsButton"
                        v-tooltip="getShiftsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageShifts && isDriverFiltered"
                        :class="{ 'current-page': isCurrentPageShifts && isDriverFiltered }"
                        @onClick="handleNavigate('shifts', close)"
                    >
                        <i class="ri-repeat-line"></i>
                        <span class="button-hotkey">S</span>
                    </Button>

                    <Button
                        v-if="showTripsButton"
                        v-tooltip="getTripsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageTrips && isDriverFiltered"
                        :class="{ 'current-page': isCurrentPageTrips && isDriverFiltered }"
                        @onClick="handleNavigate('trips', close)"
                    >
                        <i class="ri-steering-2-line"></i>
                        <span class="button-hotkey">E</span>
                    </Button>

                    <Button
                        v-if="showCardPaymentsButton"
                        v-tooltip="getCardPaymentsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageCardPayments && isDriverFiltered"
                        :class="{
                            'current-page': isCurrentPageCardPayments && isDriverFiltered,
                        }"
                        @onClick="handleNavigate('card-payments', close)"
                    >
                        <i class="ri-bank-card-line"></i>
                        <span class="button-hotkey">K</span>
                    </Button>

                    <Button
                        v-if="showEmployeesButton"
                        v-tooltip="getEmployeesTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageEmployees && isDriverFiltered"
                        :class="{
                            'current-page': isCurrentPageEmployees && isDriverFiltered,
                        }"
                        @onClick="handleNavigate('employees', close)"
                    >
                        <i class="ri-team-line"></i>
                        <span class="button-hotkey">M</span>
                    </Button>
                </div>

                <div v-if="$slots.actions" class="driver-popover-custom-actions">
                    <slot
                        name="actions"
                        :close="close"
                        :driverNumber="driverNumber"
                        :driverId="driverId"
                        :driverName="displayDriverName"
                    />
                </div>
            </div>
        </template>
    </Popover>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from '@/components/widgets/Button.vue';
import Popover from '@/components/widgets/Popover.vue';

export default {
    name: 'DriverPopover',
    components: { Button, Popover },
    props: {
        driverName: {
            type: [String, Number],
            required: true,
            default: '',
        },
        driverNumber: { type: [String, Number], required: true },
        driverId: { type: [String, Number], default: null },
        trigger: {
            type: String,
            default: 'click',
            validator: (v) => ['click'].includes(v),
        },
        showDriverNumber: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isOpen: false,
            closeFunction: null,
        };
    },
    computed: {
        ...mapGetters(['isPlanBasic']),

        displayDriverName() {
            if (!this.driverName) return '';
            if (!this.driverNumber || !this.showDriverNumber) return String(this.driverName);

            const isNumeric =
                !isNaN(this.driverName) &&
                !isNaN(parseFloat(this.driverName)) &&
                isFinite(this.driverName);

            if (isNumeric) {
                return `Fahrer: ${this.driverName}`;
            }

            return `${this.driverName} (${this.driverNumber})`;
        },

        currentRoute() {
            return this.$route.path.split('/')[1];
        },

        showShiftsButton() {
            return this.isPlanBasic;
        },

        showTripsButton() {
            return this.isPlanBasic;
        },

        showWorkingTimeButton() {
            return this.isPlanBasic;
        },

        showRevenueButton() {
            return this.isPlanBasic;
        },

        showCardPaymentsButton() {
            return this.isPlanBasic;
        },

        showEmployeesButton() {
            return this.isPlanBasic;
        },

        isCurrentPageShifts() {
            return this.currentRoute === 'shifts';
        },

        isCurrentPageTrips() {
            return this.currentRoute === 'trips';
        },

        isCurrentPageWorkingTime() {
            return this.currentRoute === 'working-time';
        },

        isCurrentPageRevenue() {
            return this.currentRoute === 'revenue';
        },

        isCurrentPageCardPayments() {
            return this.currentRoute === 'card-payments';
        },

        isCurrentPageEmployees() {
            return this.currentRoute === 'employees';
        },

        canFilterCurrentPage() {
            const filterablePages = [
                'shifts',
                'trips',
                'working-time',
                'operationride',
                'employees',
            ];
            return filterablePages.includes(this.currentRoute);
        },

        isDriverFiltered() {
            const currentDriver = this.$route.query.driver;
            return currentDriver === String(this.driverNumber);
        },

        availableHotkeys() {
            const hotkeys = [];

            if (this.showShiftsButton) {
                hotkeys.push({ key: 'S', label: 'Schichten' });
            }

            if (this.showTripsButton) {
                hotkeys.push({ key: 'E', label: 'Einzelfahrten' });
            }

            if (this.showWorkingTimeButton) {
                hotkeys.push({ key: 'A', label: 'Arbeitszeiten' });
            }

            if (this.showRevenueButton) {
                hotkeys.push({ key: 'U', label: 'Umsatz' });
            }

            if (this.showCardPaymentsButton) {
                hotkeys.push({ key: 'K', label: 'Kartenzahlungen' });
            }

            if (this.showEmployeesButton) {
                hotkeys.push({ key: 'M', label: 'Mitarbeiter' });
            }

            return hotkeys;
        },
    },
    methods: {
        handleOpen() {
            this.isOpen = true;
            document.addEventListener('keydown', this.handleKeydown);

            this.$emit('open', {
                driverName: this.displayDriverName,
                driverNumber: this.driverNumber,
                driverId: this.driverId,
            });
        },
        handleClose() {
            this.isOpen = false;
            this.closeFunction = null;
            document.removeEventListener('keydown', this.handleKeydown);

            this.$emit('close', {
                driverName: this.displayDriverName,
                driverNumber: this.driverNumber,
                driverId: this.driverId,
            });
        },
        setCloseFunction(close) {
            this.closeFunction = close;
            return true;
        },
        handleKeydown(e) {
            if (!this.isOpen || !this.closeFunction) return;

            switch (e.key.toLowerCase()) {
                case 's':
                    if (this.showShiftsButton) {
                        e.preventDefault();
                        this.handleNavigate('shifts', this.closeFunction);
                    }
                    break;
                case 'e':
                    if (this.showTripsButton) {
                        e.preventDefault();
                        this.handleNavigate('trips', this.closeFunction);
                    } else if (this.currentRoute === 'shifts') {
                        e.preventDefault();
                        this.handleChangeDriver(this.closeFunction);
                    }
                    break;
                case 'a':
                    if (this.showWorkingTimeButton) {
                        e.preventDefault();
                        this.handleNavigate('working-time', this.closeFunction);
                    }
                    break;
                case 'u':
                    if (this.showRevenueButton) {
                        e.preventDefault();
                        this.handleNavigate('revenue', this.closeFunction);
                    }
                    break;
                case 'k':
                    if (this.showCardPaymentsButton) {
                        e.preventDefault();
                        this.handleNavigate('card-payments', this.closeFunction);
                    }
                    break;
                case 'm':
                    if (this.showEmployeesButton) {
                        e.preventDefault();
                        this.handleNavigate('employees', this.closeFunction);
                    }
                    break;
            }
        },
        handleNavigate(page, close) {
            const query = {
                driver: this.driverNumber,
            };

            const currentQuery = this.$route.query;
            if (currentQuery.from) query.from = currentQuery.from;
            if (currentQuery.to) query.to = currentQuery.to;
            if (currentQuery.car) query.car = currentQuery.car;

            if (this.isCurrentPage(page) && !this.isDriverFiltered) {
                this.$router.push({ path: `/${page}`, query });
                this.$emit('filter', {
                    driverId: this.driverId || this.driverNumber,
                    driverName: this.displayDriverName,
                });
                close();
                return;
            }

            this.$router.push({ path: `/${page}`, query });
            close();
        },
        handleFilterCurrentPage(close) {
            this.$emit('filter', {
                driverId: this.driverId || this.driverNumber,
                driverName: this.displayDriverName,
            });
            close();
        },
        handleChangeDriver(close) {
            this.$emit('changeDriver', {
                driverId: this.driverId || this.driverNumber,
                driverName: this.displayDriverName,
            });
            close();
        },

        getRevenueTooltip() {
            if (this.isCurrentPageRevenue) {
                return this.isDriverFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrer filtern (U)';
            }
            return 'Im Umsatz Anzeigen (U)';
        },
        getWorkingTimeTooltip() {
            if (this.isCurrentPageWorkingTime) {
                return this.isDriverFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrer filtern (A)';
            }
            return 'In Arbeitszeiten Anzeigen (A)';
        },
        getShiftsTooltip() {
            if (this.isCurrentPageShifts) {
                return this.isDriverFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrer filtern (S)';
            }
            return 'In Schichten Anzeigen (S)';
        },
        getTripsTooltip() {
            if (this.isCurrentPageTrips) {
                return this.isDriverFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrer filtern (E)';
            }
            return 'In Einzelfahrten Anzeigen (E)';
        },
        getCardPaymentsTooltip() {
            if (this.isCurrentPageCardPayments) {
                return this.isDriverFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrer filtern (K)';
            }
            return 'In Kartenzahlungen Anzeigen (K)';
        },
        getEmployeesTooltip() {
            if (this.isCurrentPageEmployees) {
                return this.isDriverFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrer filtern (M)';
            }
            return 'In Mitarbeiter Anzeigen (M)';
        },
        isCurrentPage(page) {
            return this.currentRoute === page;
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);
    },
};
</script>

<style lang="scss" scoped>
.driver-popover-default-trigger {
    font-weight: 500;
    color: var(--color-blue-dark);
    padding: 2px 4px;
    border-radius: 3px;
    border: 0.5px solid rgba(167, 167, 167, 0.3);
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: rgba(167, 167, 167, 0.2);

    &:hover {
        background-color: rgba(167, 167, 167, 0.4);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
}
.driver-popover-content {
    display: flex;
    flex-direction: column;
}
.driver-info {
    margin-bottom: 4px;
    .driver-name-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 1px;
    }
    .driver-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--color-text-black);
        flex: 1;
    }
    .driver-number {
        font-size: 0.75rem;
        color: var(--color-gray);
    }
}
.driver-popover-actions {
    margin-top: 6px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;

    .Button {
        i {
            width: 1em;
            text-align: center;
        }
    }
}
.driver-popover-custom-actions {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 0.5px solid rgba(0, 0, 0, 0.08);
}
.driver-popover-hotkeys {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 0.5px solid rgba(0, 0, 0, 0.08);
}
.hotkey-hint {
    font-size: 0.75rem;
    color: var(--color-gray);
    line-height: 1.4;
}
.hotkey {
    font-weight: 600;
    color: var(--color-text-black);
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 4px;
    border-radius: 3px;
    margin-right: 4px;
}
.button-hotkey {
    font-weight: 600;
    color: var(--color-text-black);
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 4px;
    border-radius: 3px;
    margin-left: 4px;
    font-size: 1rem;
    font-family: 'Courier New', monospace;
}

.current-page {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
        opacity: 0.5;
    }

    .button-hotkey {
        opacity: 0.5;
    }
}
</style>
